<script setup lang="ts">
import { onBeforeUnmount, ref } from 'vue';
import { fetchLabelBlobUrl, LabelNotReadyError, type LabelFormat } from '../lib/labels';

const props = defineProps<{ shipmentIds: number[] }>();

const blobUrl = ref<string | null>(null);
const loading = ref(false);
const error = ref<string | null>(null);
const notReady = ref(false);
const format = ref<LabelFormat>('A6');

function revoke(): void {
  if (blobUrl.value) {
    URL.revokeObjectURL(blobUrl.value);
    blobUrl.value = null;
  }
}

async function load(): Promise<void> {
  loading.value = true;
  error.value = null;
  notReady.value = false;
  revoke();

  try {
    blobUrl.value = await fetchLabelBlobUrl(props.shipmentIds, format.value);
  } catch (caught) {
    if (caught instanceof LabelNotReadyError) {
      notReady.value = true;
    } else {
      error.value = caught instanceof Error ? caught.message : String(caught);
    }
  } finally {
    loading.value = false;
  }
}

// Blob URLs hold the PDF in memory until explicitly released.
onBeforeUnmount(revoke);
</script>

<template>
  <div class="label">
    <div class="label__bar">
      <span class="label__title">
        Label voor zending {{ props.shipmentIds.join(', ') }}
      </span>

      <select v-model="format" class="label__format" aria-label="Labelformaat">
        <option value="A6">A6</option>
        <option value="A4">A4</option>
      </select>

      <button class="btn btn--small" type="button" :disabled="loading" @click="load">
        {{ blobUrl ? 'Opnieuw laden' : 'Label tonen' }}
      </button>

      <a v-if="blobUrl" class="btn btn--small" :href="blobUrl" target="_blank" rel="noopener">
        Openen
      </a>
    </div>

    <p v-if="loading" class="label__status">Label ophalen…</p>
    <p v-else-if="notReady" class="label__status">
      Het label is nog niet gerenderd. Probeer het over een paar seconden opnieuw.
    </p>
    <p v-else-if="error" class="label__status label__status--error">{{ error }}</p>

    <iframe
      v-if="blobUrl"
      :src="blobUrl"
      class="label__frame"
      title="Labelvoorbeeld"
    ></iframe>
  </div>
</template>

<style scoped>
.label {
  border-top: 1px solid var(--border);
  padding: 10px 12px 12px;
}

.label__bar {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.label__title {
  flex: 1 1 auto;
  font-size: 13px;
  color: var(--text-muted);
}

.label__format {
  padding: 4px 6px;
  font-size: 13px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--surface);
  color: var(--text);
}

.label__status {
  margin: 10px 0 0;
  font-size: 13px;
  color: var(--text-muted);
}

.label__status--error {
  color: var(--danger);
}

.label__frame {
  width: 100%;
  height: 420px;
  margin-top: 10px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: #fff;
}
</style>
